// Sticky toolbar above `<ChatTable>`. See docs/design/chat-table-toolbar.md.
import { useAuiState } from "@assistant-ui/react";
import { useDebouncedEffect } from "@react-hookz/web";
import { Download, LayoutGrid, Loader2 } from "lucide-react";
import { useRef, useState } from "react";
import { useParams } from "react-router";

import type { ICitationResolution } from "@/api/chat/chat.types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useGenerateArtifactMutation } from "@/hooks/useGenerateArtifactMutation";
import { trackArtifactExported } from "@/lib/analytics";
import { cn } from "@/lib/utils";

import {
    citationHandleResolver,
    toArtifactCell,
} from "./utils/chat-table-to-artifact";
import type { ChatTableData } from "./utils/parse-markdown-table";
import { EditorialInlineText } from "./editorial-inline-text";
import { hasVisibleDisplay } from "./utils/emoji-typesetting";
import { titleToFilename } from "./utils/title-to-filename";

const FILTER_DEBOUNCE_MS = 150;

export interface ChatTableToolbarProps {
    title?: string;
    /** Unfiltered row count, captured by the parent at render time. */
    rowCountUnfiltered: number;
    columnCount: number;
    data: ChatTableData;
    // Should be referentially stable: the debounce closure captures
    // this on each effect arm, so an unstable identity could fire a
    // stale handler against state from a prior render.
    setGlobalFilter: (value: string) => void;
}

export function ChatTableToolbar({
    title,
    rowCountUnfiltered,
    columnCount,
    data,
    setGlobalFilter,
}: ChatTableToolbarProps) {
    const role = useAuiState((s) => s.message.role);
    const isStreaming = useAuiState(
        (s) => s.message.status?.type === "running"
    );
    // Native cite:// cells carry only a handle; the per-message resolution map
    // (set by the citation_resolved stream event, rehydrated on reload) recovers
    // the document/page the backend needs to hyperlink the exported cell.
    const citationResolutions = useAuiState(
        (s) =>
            s.message.metadata?.custom?.citation_resolutions as
                | ICitationResolution[]
                | undefined
    );
    const mutation = useGenerateArtifactMutation();
    const { chatId } = useParams<{ chatId: string }>();

    const [filterInput, setFilterInput] = useState("");
    // The ref gates the bubble until the user actually types; without
    // it, `useDebouncedEffect`'s on-mount fire would dispatch a
    // spurious `setGlobalFilter("")` to the parent. Backspace-to-empty
    // still emits because `hasTypedRef` flips on the first onChange.
    const hasTypedRef = useRef(false);
    useDebouncedEffect(
        () => {
            if (!hasTypedRef.current) return;
            setGlobalFilter(filterInput);
        },
        [filterInput],
        FILTER_DEBOUNCE_MS
    );

    if (role !== "assistant" || isStreaming) return null;

    const onDownload = () => {
        const headers = data.headers.map((h) => h.text);
        const resolve = citationHandleResolver(citationResolutions);
        const rows = data.rows.map((row) =>
            row.map((c) => toArtifactCell(c, resolve))
        );
        mutation.mutate(
            {
                type: "xlsx",
                tables: [{ headers, rows }],
                title,
                filename: titleToFilename(title),
                ...(chatId ? { chat_id: chatId } : {}),
            },
            {
                onSuccess: () => {
                    trackArtifactExported({
                        chat_id: chatId ?? null,
                        artifact_type: "xlsx",
                        row_count: rowCountUnfiltered,
                        column_count: columnCount,
                    });
                },
            }
        );
    };

    const isPending = mutation.isPending;
    const buttonLabel = isPending ? "Preparing Excel…" : "Excel";
    // Visible label is "Excel"; accessible name keeps the verb so
    // screen readers announce a recognisable action.
    const ariaLabel = isPending ? "Preparing Excel" : "Download as Excel";

    return (
        <div
            className={cn(
                "aui-chat-table-toolbar bg-muted/40 sticky top-0 z-10",
                "flex flex-wrap items-center justify-between gap-2 border-b px-3 py-2"
            )}
        >
            <div className="flex min-w-0 items-center gap-2">
                <LayoutGrid
                    aria-hidden
                    className="text-muted-foreground h-4 w-4 shrink-0"
                />
                {title && hasVisibleDisplay(title) && (
                    <span
                        data-testid="chat-table-toolbar-title"
                        className="truncate text-sm font-semibold text-slate-700"
                    >
                        <EditorialInlineText text={title} usage="inline" />
                    </span>
                )}
                <span className="text-muted-foreground shrink-0 text-xs tabular-nums">
                    {rowCountUnfiltered}{" "}
                    {rowCountUnfiltered === 1 ? "row" : "rows"} · {columnCount}{" "}
                    {columnCount === 1 ? "col" : "cols"}
                </span>
            </div>
            <div className="flex min-w-0 flex-1 items-center gap-2 sm:flex-initial">
                <Input
                    type="text"
                    placeholder="Filter rows…"
                    value={filterInput}
                    onChange={(e) => {
                        hasTypedRef.current = true;
                        setFilterInput(e.target.value);
                    }}
                    className="h-8 w-full min-w-24 sm:w-48"
                    aria-label="Filter rows"
                />
                <Button
                    variant="default"
                    size="sm"
                    onClick={onDownload}
                    disabled={isPending}
                    aria-busy={isPending || undefined}
                    aria-disabled={isPending || undefined}
                    aria-label={ariaLabel}
                >
                    {isPending ? (
                        <Loader2
                            data-icon="inline-start"
                            className="animate-spin motion-reduce:animate-none"
                        />
                    ) : (
                        <Download data-icon="inline-start" />
                    )}
                    {buttonLabel}
                </Button>
            </div>
        </div>
    );
}
